/**
 * Admin Layout
 *
 * Top chrome with centered pill navigation, optional section sub-nav,
 * and a wide content column for dashboard-style pages.
 */

import { useEffect, useState, useRef } from 'react'
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useAppDispatch, useAppSelector } from '@/app/hooks'
import { clearCredentials } from '@/features/auth/slices/authSlice'
import { useLogoutMutation } from '@/features/auth/api/authApi'
import { clearAuthData } from '@/core/auth'
import { logError } from '@/utils/logger'
import {
  Bell,
  ChevronDown,
  LogOut,
  Search,
  Settings,
  User,
} from 'lucide-react'

const pageVariants = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
}

const pageTransition = {
  type: 'tween',
  ease: 'anticipate',
  duration: 0.3,
}

const mainNav = [
  { label: 'Dashboard', to: '/admin', end: true },
  { label: 'Users', to: '/admin/users' },
  { label: 'Interest', to: '/admin/interest' },
  {
    label: 'Notifications',
    to: '/admin/notifications',
    subNav: [
      { to: '/admin/notifications/push', label: 'Push notification' },
      { to: '/admin/notifications/send', label: 'Send notification list' },
    ],
  },
  {
    label: 'CMS',
    to: '/admin/cms',
    subNav: [
      { to: '/admin/cms/faq', label: 'FAQ' },
      { to: '/admin/cms/about', label: 'About us' },
    ],
  },
  {
    label: 'Posts',
    to: '/admin/posts',
    subNav: [
      { to: '/admin/posts/list', label: 'Posts' },
      { to: '/admin/posts/reported', label: 'Reported posts' },
    ],
  },
  {
    label: 'Settings',
    to: '/admin/settings',
    subNav: [
      { to: '/admin/settings/password', label: 'Change password' },
      { to: '/admin/settings/support', label: 'Support' },
      { to: '/admin/settings/keys', label: 'Keys' },
      { to: '/admin/settings/timezone', label: 'Timezone' },
    ],
  },
] as const

function TimeDisplay() {
  const [time, setTime] = useState(
    new Date().toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    })
  )

  useEffect(() => {
    const interval = setInterval(() => {
      setTime(
        new Date().toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: true,
        })
      )
    }, 1000)
    return () => clearInterval(interval)
  }, [])

  return <span className="text-sm tabular-nums text-gray-600">{time}</span>
}

function navLinkClass(active: boolean) {
  return `rounded-pill px-3.5 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
    active ? 'bg-brand text-white shadow-sm' : 'text-gray-600 hover:bg-white/90 hover:text-gray-900'
  }`
}

export function AdminLayout() {
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [logout] = useLogoutMutation()
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const authUser = useAppSelector(state => state.auth.user)
  const profileLabel = authUser?.name?.trim() || authUser?.email || 'Admin'

  const activeSubNav = mainNav.find(
    item =>
      'subNav' in item &&
      (location.pathname === item.to || location.pathname.startsWith(`${item.to}/`))
  )

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false)
      }
    }
    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [dropdownOpen])

  const handleLogout = async () => {
    try {
      dispatch(clearCredentials())
      clearAuthData()
      await logout().unwrap()
    } catch (error) {
      logError('Logout failed', error)
    } finally {
      navigate('/auth/login', { replace: true })
    }
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#f8f8fb]">
      <header className="sticky top-0 z-40 border-b border-gray-200/80 bg-white/95 shadow-sm backdrop-blur-sm">
        <div className="mx-auto flex max-w-content items-center justify-between gap-4 px-4 py-3 sm:px-6 lg:px-8">
          <Link
            to="/admin"
            className="shrink-0 text-lg font-semibold tracking-tight text-gray-900"
          >
            //stride
          </Link>

          <nav
            className="hidden min-w-0 flex-1 justify-center overflow-x-auto md:flex"
            aria-label="Primary"
          >
            <div className="inline-flex max-w-full gap-1 rounded-pill border border-gray-200/90 bg-gray-100/90 p-1.5 shadow-inset">
              {mainNav.map(item => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={'end' in item ? item.end : false}
                  className={({ isActive }) => navLinkClass(isActive)}
                >
                  {item.label}
                </NavLink>
              ))}
            </div>
          </nav>

          <div className="flex shrink-0 items-center gap-1 sm:gap-2">
            <button
              type="button"
              className="rounded-shell-inner p-2.5 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900"
              aria-label="Search"
            >
              <Search className="h-5 w-5" />
            </button>
            <button
              type="button"
              className="rounded-shell-inner p-2.5 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900"
              aria-label="Notifications"
            >
              <Bell className="h-5 w-5" />
            </button>
            <NavLink
              to="/admin/settings"
              className={({ isActive }) =>
                `rounded-shell-inner p-2.5 transition-colors hover:bg-gray-100 ${
                  isActive ? 'bg-brand-muted text-brand' : 'text-gray-600 hover:text-gray-900'
                }`
              }
              aria-label="Settings"
            >
              <Settings className="h-5 w-5" />
            </NavLink>
            <span className="hidden sm:inline">
              <TimeDisplay />
            </span>
            <div className="relative" ref={dropdownRef}>
              <button
                type="button"
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`flex items-center gap-2 rounded-shell-inner py-1.5 pl-1 pr-2 transition-colors hover:bg-gray-100 ${
                  location.pathname === '/admin/profile' ? 'bg-brand-muted ring-1 ring-brand/20' : ''
                }`}
                aria-expanded={dropdownOpen}
                aria-haspopup="menu"
              >
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-muted text-sm font-semibold text-brand">
                  {profileLabel.slice(0, 2).toUpperCase()}
                </div>
                <span className="hidden max-w-[8rem] truncate text-sm font-medium text-gray-900 lg:inline">
                  {profileLabel}
                </span>
                <ChevronDown
                  className={`hidden h-4 w-4 shrink-0 text-gray-500 transition-transform lg:block ${
                    dropdownOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>
              {dropdownOpen && (
                <div
                  className="absolute right-0 z-50 mt-2 w-52 rounded-shell-inner border border-gray-200 bg-white py-1 shadow-shell"
                  role="menu"
                >
                  <div className="border-b border-gray-100 px-4 py-2.5">
                    <p className="truncate text-sm font-medium text-gray-900">{profileLabel}</p>
                    {authUser?.email && (
                      <p className="truncate text-xs text-gray-500">{authUser.email}</p>
                    )}
                  </div>
                  <NavLink
                    to="/admin/profile"
                    role="menuitem"
                    onClick={() => setDropdownOpen(false)}
                    className={({ isActive }) =>
                      `flex w-full items-center gap-3 px-4 py-2.5 text-sm transition-colors hover:bg-gray-50 ${
                        isActive ? 'bg-brand-muted/50 font-medium text-brand' : 'text-gray-700'
                      }`
                    }
                  >
                    <User className="h-4 w-4" />
                    <span>Profile</span>
                  </NavLink>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={handleLogout}
                    className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm text-gray-700 transition-colors hover:bg-gray-50"
                  >
                    <LogOut className="h-4 w-4" />
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="border-t border-gray-100 md:hidden">
          <nav
            className="mx-auto flex max-w-content gap-1 overflow-x-auto px-4 py-2 sm:px-6"
            aria-label="Primary mobile"
          >
            {mainNav.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                end={'end' in item ? item.end : false}
                className={({ isActive }) => navLinkClass(isActive)}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>

        {activeSubNav && 'subNav' in activeSubNav && (
          <div className="border-t border-gray-100 bg-gray-50/80">
            <nav
              className="mx-auto flex max-w-content flex-wrap justify-center gap-2 px-4 py-3 sm:px-6 lg:px-8"
              aria-label="Section"
            >
              {activeSubNav.subNav.map(sub => (
                <NavLink
                  key={sub.to}
                  to={sub.to}
                  className={({ isActive }) =>
                    `rounded-pill px-4 py-1.5 text-xs font-medium transition-colors sm:text-sm ${
                      isActive
                        ? 'bg-brand text-white shadow-sm'
                        : 'bg-white text-gray-600 shadow-sm ring-1 ring-gray-200/80 hover:text-gray-900'
                    }`
                  }
                >
                  {sub.label}
                </NavLink>
              ))}
            </nav>
          </div>
        )}
      </header>

      <main className="mx-auto w-full max-w-content flex-1 px-4 py-6 sm:px-6 sm:py-8 lg:px-8">
        <motion.div
          initial="initial"
          animate="animate"
          exit="exit"
          variants={pageVariants}
          transition={pageTransition}
        >
          <Outlet />
        </motion.div>
      </main>
    </div>
  )
}
